import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, UserPlus, Mail, Shield, Eye, Edit, Crown, Trash2, Copy, Clock, AlertCircle, Bell, BellOff, CreditCard, CheckCircle, XCircle, Settings, Loader2, Building2, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_INFO = {
  owner: { label: 'Propietario', icon: Crown, color: 'bg-amber-500 text-black', description: 'Control total del sistema' },
  admin: { label: 'Administrador', icon: Edit, color: 'bg-blue-500 text-white', description: 'Puede crear y editar información' },
  viewer: { label: 'Visualizador', icon: Eye, color: 'bg-zinc-600 text-white', description: 'Solo puede ver información' }
};

const PLAN_INFO = {
  basic: { label: 'Básico', maxUsers: 1, color: 'text-zinc-400' },
  control: { label: 'Control', maxUsers: 3, color: 'text-zinc-400' },
  pro: { label: 'Profesional', maxUsers: 5, color: 'text-blue-400' },
  profesional: { label: 'Profesional', maxUsers: 5, color: 'text-blue-400' },
  enterprise: { label: 'Enterprise', maxUsers: 10, color: 'text-amber-400' }
};

export default function Equipo() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountInfo, setAccountInfo] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteFraccionamiento, setInviteFraccionamiento] = useState('todos');
  const [fraccionamientos, setFraccionamientos] = useState([]);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [activeTab, setActiveTab] = useState('transparencia');
  
  // Estados para suscripción
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Estados para notificaciones
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchFraccionamientos();
    fetchSubscriptionStatus();
    fetchNotificationConfig();
  }, []);

  const fetchFraccionamientos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFraccionamientos(data);
      }
    } catch (error) {
      console.error('Error fetching fraccionamientos:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        await Promise.all([fetchAccountInfo(), fetchTeamUsers(), fetchInvitations()]);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountInfo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/account`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAccountInfo(data);
      }
    } catch (error) {
      console.error('Error fetching account info:', error);
    }
  };

  const fetchTeamUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTeamUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching team users:', error);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/invitations`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/status`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionInfo(data);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchNotificationConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notificaciones/config`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setNotificationsEnabled(data.notificaciones_email);
      }
    } catch (error) {
      console.error('Error fetching notifications config:', error);
    }
  };

  const toggleNotifications = async (enabled) => {
    setLoadingNotifications(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/notificaciones/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notificaciones_email: enabled })
      });
      if (response.ok) {
        setNotificationsEnabled(enabled);
        toast.success(enabled ? 'Notificaciones activadas' : 'Notificaciones desactivadas');
      }
    } catch (error) {
      toast.error('Error al actualizar notificaciones');
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      toast.error('Ingresa un correo electrónico');
      return;
    }

    setSendingInvite(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          email: inviteEmail, 
          role: inviteRole,
          fraccionamiento_id: inviteFraccionamiento === 'todos' ? null : inviteFraccionamiento
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al enviar invitación');
      }

      toast.success(`Invitación enviada a ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('viewer');
      setInviteFraccionamiento('todos');
      fetchInvitations();
      fetchAccountInfo();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/invitations/${invitationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al cancelar invitación');
      }

      toast.success('Invitación cancelada');
      fetchInvitations();
      fetchAccountInfo();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/users/${userId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al cambiar rol');
      }

      toast.success(`Rol actualizado a ${ROLE_INFO[newRole]?.label}`);
      fetchTeamUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!confirm('¿Estás seguro de remover a este usuario del equipo?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/team/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al remover usuario');
      }

      toast.success('Usuario removido del equipo');
      fetchTeamUsers();
      fetchAccountInfo();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyInviteLink = (token) => {
    const url = `${window.location.origin}/invitation/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Enlace copiado al portapapeles');
  };

  const handleSubscribe = async () => {
    setProcessingPayment(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error creando sesión de pago');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message || 'Error al procesar. Intenta de nuevo.');
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('¿Estás seguro de cancelar tu suscripción? Mantendrás acceso hasta el fin del período actual.')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Suscripción cancelada. Mantendrás acceso hasta el fin del período.');
        fetchSubscriptionStatus();
      } else {
        throw new Error('Error cancelando');
      }
    } catch {
      toast.error('Error al cancelar. Intenta de nuevo.');
    }
  };

  const isOwner = user?.account_role === 'owner';
  const plan = accountInfo?.plan || 'basic';
  const usersCount = accountInfo?.users_count || 1;
  const maxUsers = accountInfo?.users_limit || PLAN_INFO[plan]?.maxUsers || 1;
  const pendingInvitations = invitations.filter(i => !i.is_expired).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const getStatusBadge = () => {
    if (!subscriptionInfo) return null;
    
    switch (subscriptionInfo.status) {
      case 'trial':
        return <Badge className="bg-blue-600 text-white">Trial Activo</Badge>;
      case 'active':
        return <Badge className="bg-green-600 text-white">Activo</Badge>;
      case 'expired':
        return <Badge className="bg-red-600 text-white">Expirado</Badge>;
      case 'cancelled':
        return <Badge className="bg-yellow-600 text-black">Cancelado</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Settings className="w-7 h-7 text-amber-500" />
            Configuración
          </h1>
          <p className="text-zinc-400 mt-1">Gestiona tu cuenta, equipo y preferencias</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-zinc-900 border border-zinc-800 h-14 p-1 rounded-xl mb-6">
          <TabsTrigger 
            value="transparencia" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black rounded-lg h-full text-base font-medium"
            data-testid="tab-transparencia"
          >
            <Crown className="w-4 h-4 mr-2" />
            Transparencia
          </TabsTrigger>
          <TabsTrigger 
            value="equipo" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black rounded-lg h-full text-base font-medium"
            data-testid="tab-equipo"
          >
            <Users className="w-4 h-4 mr-2" />
            Equipo
          </TabsTrigger>
          <TabsTrigger 
            value="suscripcion" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black rounded-lg h-full text-base font-medium"
            data-testid="tab-suscripcion"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Suscripción
          </TabsTrigger>
          <TabsTrigger 
            value="notificaciones" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black rounded-lg h-full text-base font-medium"
            data-testid="tab-notificaciones"
          >
            <Bell className="w-4 h-4 mr-2" />
            Notificaciones
          </TabsTrigger>
        </TabsList>

        {/* Tab: Transparencia - Sistema de Control */}
        <TabsContent value="transparencia" className="space-y-6">
          {/* Banner Principal */}
          <div className="border border-amber-500/30 rounded-xl p-6 bg-gradient-to-r from-amber-500/10 to-transparent">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Título y Estado */}
              <div className="flex items-center gap-4">
                <div className="p-4 bg-amber-500/20 rounded-xl">
                  <Crown className="w-8 h-8 text-amber-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-white text-xl">Sistema de Transparencia</h3>
                    <Badge className="bg-amber-500 text-black text-xs">
                      Plan {subscriptionInfo?.plan || 'Profesional'}
                    </Badge>
                    {subscriptionInfo?.status === 'trial' && (
                      <Badge className="bg-blue-500 text-white text-xs">
                        Trial - {subscriptionInfo?.days_remaining || 7} días
                      </Badge>
                    )}
                    {subscriptionInfo?.status === 'active' && (
                      <Badge className="bg-green-500 text-white text-xs">Activo</Badge>
                    )}
                  </div>
                  <p className="text-zinc-400 text-sm mt-2">
                    Control financiero y auditoría completa para desarrolladores inmobiliarios
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Métricas de Uso */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Usuarios */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-500 text-sm">Usuarios del Equipo</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {teamUsers?.length || 1}
                      <span className="text-zinc-500 text-lg font-normal"> / {subscriptionInfo?.limits?.max_users || 5}</span>
                    </p>
                  </div>
                  <div className="p-3 bg-amber-500/20 rounded-xl">
                    <Users className="w-6 h-6 text-amber-500" />
                  </div>
                </div>
                <div className="mt-4 h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full transition-all"
                    style={{ width: `${Math.min(((teamUsers?.length || 1) / (subscriptionInfo?.limits?.max_users || 5)) * 100, 100)}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Fraccionamientos */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-500 text-sm">Fraccionamientos</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {fraccionamientos?.length || 0}
                      <span className="text-zinc-500 text-lg font-normal"> / {(subscriptionInfo?.limits?.max_fraccionamientos || 3) >= 999 ? '∞' : subscriptionInfo?.limits?.max_fraccionamientos || 3}</span>
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-500/20 rounded-xl">
                    <Building2 className="w-6 h-6 text-emerald-500" />
                  </div>
                </div>
                <div className="mt-4 h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${Math.min(((fraccionamientos?.length || 0) / (subscriptionInfo?.limits?.max_fraccionamientos || 3)) * 100, 100)}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Estado de Cuenta */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-500 text-sm">Estado de la Cuenta</p>
                    <div className="flex items-center gap-2 mt-1">
                      {subscriptionInfo?.status === 'active' ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="text-xl font-bold text-green-500">Activa</span>
                        </>
                      ) : subscriptionInfo?.status === 'trial' ? (
                        <>
                          <Clock className="w-5 h-5 text-blue-500" />
                          <span className="text-xl font-bold text-blue-500">En Prueba</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                          <span className="text-xl font-bold text-amber-500">Pendiente</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-blue-500/20 rounded-xl">
                    <CreditCard className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
                <Link to="/precios" className="block mt-4">
                  <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold">
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                    Mejorar Plan
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Información Adicional */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">¿Qué incluye tu plan?</CardTitle>
              <CardDescription>Características de tu suscripción actual</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-zinc-300">Dashboard de métricas en tiempo real</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-zinc-300">Control de pagos y cobranza</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-zinc-300">Gestión multi-moneda (USD/MXN)</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-zinc-300">Generación de comprobantes PDF</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-zinc-300">Invitación de usuarios al equipo</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-zinc-300">Análisis de cobranza y proyecciones</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Equipo */}
        <TabsContent value="equipo" className="space-y-6">
          {/* Botón Invitar */}
          {isOwner && (
            <div className="flex justify-end">
              <Button
                onClick={() => setShowInviteModal(true)}
                className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                disabled={usersCount + pendingInvitations >= maxUsers}
                data-testid="invite-user-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invitar Usuario
              </Button>
            </div>
          )}

          {/* Plan Info Card */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${plan === 'enterprise' ? 'bg-amber-500/20' : (plan === 'pro' || plan === 'profesional') ? 'bg-blue-500/20' : plan === 'control' ? 'bg-zinc-700' : 'bg-zinc-800'}`}>
                    <Shield className={`w-6 h-6 ${PLAN_INFO[plan]?.color || 'text-zinc-400'}`} />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400">Plan actual</p>
                    <p className={`text-xl font-bold ${PLAN_INFO[plan]?.color || 'text-white'}`}>
                      {PLAN_INFO[plan]?.label || plan?.charAt(0).toUpperCase() + plan?.slice(1) || 'Básico'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{usersCount}</p>
                    <p className="text-sm text-zinc-400">de {maxUsers} usuarios</p>
                  </div>
                  
                  <div className="w-32 h-2 bg-zinc-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${usersCount >= maxUsers ? 'bg-red-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.min((usersCount / maxUsers) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
              
              {usersCount >= maxUsers && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-400 text-sm">
                    Has alcanzado el límite de usuarios. Ve a Suscripción para mejorar tu plan.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Team Members */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="border-b border-zinc-800">
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-500" />
                Miembros del Equipo ({teamUsers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-zinc-800">
                {teamUsers.map((member) => {
                  const role = member.account_role || 'viewer';
                  const roleInfo = ROLE_INFO[role] || ROLE_INFO.viewer;
                  const RoleIcon = roleInfo.icon;
                  const isCurrentUser = member.user_id === user?.user_id;
                  
                  return (
                    <div key={member.user_id} className="p-4 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 font-bold">
                          {member.name?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="text-white font-medium flex items-center gap-2">
                            {member.name || 'Usuario'}
                            {isCurrentUser && <span className="text-xs text-zinc-500">(Tú)</span>}
                          </p>
                          <p className="text-sm text-zinc-400">{member.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Badge className={`${roleInfo.color} flex items-center gap-1`}>
                          <RoleIcon className="w-3 h-3" />
                          {roleInfo.label}
                        </Badge>
                        
                        {isOwner && !isCurrentUser && role !== 'owner' && (
                          <div className="flex items-center gap-2">
                            <Select 
                              value={role} 
                              onValueChange={(value) => handleChangeRole(member.user_id, value)}
                            >
                              <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                <SelectItem value="admin" className="text-white">Administrador</SelectItem>
                                <SelectItem value="viewer" className="text-white">Visualizador</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveUser(member.user_id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Pending Invitations */}
          {isOwner && invitations.length > 0 && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="border-b border-zinc-800">
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-amber-500" />
                  Invitaciones Pendientes ({invitations.filter(i => !i.is_expired).length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-zinc-800">
                  {invitations.map((invitation) => {
                    const roleInfo = ROLE_INFO[invitation.role] || ROLE_INFO.viewer;
                    const isExpired = invitation.is_expired;
                    
                    return (
                      <div key={invitation.invitation_id} className={`p-4 flex items-center justify-between ${isExpired ? 'opacity-50' : ''}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full ${isExpired ? 'bg-zinc-700' : 'bg-blue-500/20'} flex items-center justify-center`}>
                            <Mail className={`w-5 h-5 ${isExpired ? 'text-zinc-500' : 'text-blue-400'}`} />
                          </div>
                          <div>
                            <p className="text-white">{invitation.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={roleInfo.color} variant="secondary">
                                {roleInfo.label}
                              </Badge>
                              {isExpired ? (
                                <span className="text-xs text-red-400 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" /> Expirada
                                </span>
                              ) : (
                                <span className="text-xs text-zinc-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> Pendiente
                                </span>
                              )}
                              {invitation.fraccionamiento_nombre && (
                                <span className="text-xs text-amber-400 flex items-center gap-1">
                                  Solo: {invitation.fraccionamiento_nombre}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {!isExpired && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyInviteLink(invitation.token)}
                              className="text-zinc-400 hover:text-white"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelInvitation(invitation.invitation_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Role Permissions Info */}
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(ROLE_INFO).map(([key, info]) => {
              const Icon = info.icon;
              return (
                <Card key={key} className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={info.color}>
                        <Icon className="w-3 h-3 mr-1" />
                        {info.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-zinc-400">{info.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Tab: Suscripción */}
        <TabsContent value="suscripcion" className="space-y-6">
          {/* Processing Payment */}
          {processingPayment && (
            <Card className="bg-zinc-900 border-amber-500/50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                  <span className="text-white">Procesando pago...</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Planes para cada etapa de tu negocio - ARRIBA */}
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Planes para cada etapa de tu negocio</h2>
              <p className="text-zinc-400">Elige el plan que mejor se adapte a tus necesidades</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Plan Control */}
              <Card className="bg-zinc-900 border-zinc-700 hover:border-zinc-600 transition-colors" data-testid="plan-control">
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-white text-xl">Control</CardTitle>
                  <CardDescription className="text-zinc-400">Para empezar</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-white">$49</span>
                    <span className="text-zinc-400 ml-1">USD/mes</span>
                  </div>
                  <p className="text-sm text-zinc-500 mb-4">$470 USD/año (ahorra 20%)</p>
                  
                  <ul className="space-y-3 text-left mb-6">
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span><strong>1</strong> Fraccionamiento</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span><strong>3</strong> Usuarios incluidos</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>$10 USD/mes por usuario extra</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Control de lotes y clientes</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Sistema de pagos</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Dashboard con KPIs</span>
                    </li>
                  </ul>

                  <Button
                    onClick={handleSubscribe}
                    disabled={processingPayment}
                    variant="outline"
                    className="w-full border-zinc-600 text-white hover:bg-zinc-800"
                    data-testid="select-plan-control"
                  >
                    Seleccionar Plan
                  </Button>
                </CardContent>
              </Card>

              {/* Plan Profesional (Destacado) */}
              <Card className="bg-zinc-900 border-amber-500 relative" data-testid="plan-profesional">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-amber-500 text-black font-semibold px-3">Más Popular</Badge>
                </div>
                <CardHeader className="text-center pb-2 pt-6">
                  <CardTitle className="text-amber-500 text-xl">Profesional</CardTitle>
                  <CardDescription className="text-zinc-400">Para crecer</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-amber-500">$89</span>
                    <span className="text-zinc-400 ml-1">USD/mes</span>
                  </div>
                  <p className="text-sm text-zinc-500 mb-4">$854 USD/año (ahorra 20%)</p>
                  
                  <ul className="space-y-3 text-left mb-6">
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span><strong>3</strong> Fraccionamientos</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span><strong>5</strong> Usuarios incluidos</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span>$8 USD/mes por usuario extra</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span>Todo lo del plan Control</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span>Notificaciones por email</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span>Soporte prioritario</span>
                    </li>
                  </ul>

                  <Button
                    onClick={handleSubscribe}
                    disabled={processingPayment}
                    className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold"
                    data-testid="select-plan-profesional"
                  >
                    {processingPayment ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : null}
                    Seleccionar Plan
                  </Button>
                </CardContent>
              </Card>

              {/* Plan Enterprise */}
              <Card className="bg-zinc-900 border-zinc-700 hover:border-zinc-600 transition-colors" data-testid="plan-enterprise">
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-white text-xl">Enterprise</CardTitle>
                  <CardDescription className="text-zinc-400">Sin límites</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-white">$149</span>
                    <span className="text-zinc-400 ml-1">USD/mes</span>
                  </div>
                  <p className="text-sm text-zinc-500 mb-4">$1,430 USD/año (ahorra 20%)</p>
                  
                  <ul className="space-y-3 text-left mb-6">
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Fraccionamientos <strong>ilimitados</strong></span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span><strong>10</strong> Usuarios incluidos</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>$7 USD/mes por usuario extra</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Todo lo del plan Profesional</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Soporte dedicado</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Onboarding personalizado</span>
                    </li>
                  </ul>

                  <Button
                    onClick={handleSubscribe}
                    disabled={processingPayment}
                    variant="outline"
                    className="w-full border-zinc-600 text-white hover:bg-zinc-800"
                    data-testid="select-plan-enterprise"
                  >
                    Seleccionar Plan
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Info adicional */}
            <div className="text-center mt-6">
              <p className="text-zinc-500 text-sm">
                Sin contratos • Cancela en cualquier momento • Todos los precios en USD
              </p>
            </div>
          </div>

          {/* Estado actual - ABAJO */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Estado de tu cuenta</CardTitle>
                {getStatusBadge()}
              </div>
              <CardDescription className="text-zinc-400">
                {subscriptionInfo?.email}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {subscriptionInfo?.status === 'trial' && (
                  <div className="flex items-center gap-3 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <Clock className="w-6 h-6 text-blue-500" />
                    <div>
                      <p className="text-white font-medium">Prueba gratuita</p>
                      <p className="text-zinc-400 text-sm">
                        Te quedan <span className="text-blue-400 font-bold">{subscriptionInfo.days_remaining}</span> días de prueba
                      </p>
                    </div>
                  </div>
                )}

                {subscriptionInfo?.status === 'active' && (
                  <div className="flex items-center gap-3 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="text-white font-medium">Suscripción activa</p>
                      <p className="text-zinc-400 text-sm">
                        Próxima renovación: <span className="text-green-400">{subscriptionInfo.renewal_date?.split('T')[0]}</span>
                      </p>
                    </div>
                  </div>
                )}

                {subscriptionInfo?.status === 'expired' && (
                  <div className="flex items-center gap-3 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <XCircle className="w-6 h-6 text-red-500" />
                    <div>
                      <p className="text-white font-medium">Acceso expirado</p>
                      <p className="text-zinc-400 text-sm">
                        Activa tu suscripción para continuar usando el sistema
                      </p>
                    </div>
                  </div>
                )}

                {subscriptionInfo?.status === 'cancelled' && (
                  <div className="flex items-center gap-3 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                    <XCircle className="w-6 h-6 text-yellow-500" />
                    <div>
                      <p className="text-white font-medium">Suscripción cancelada</p>
                      <p className="text-zinc-400 text-sm">
                        Acceso hasta: {subscriptionInfo.access_until?.split('T')[0] || 'Fin del período'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Botón cancelar suscripción */}
              {subscriptionInfo?.status === 'active' && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <Button
                    onClick={handleCancelSubscription}
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid="cancel-subscription-button"
                  >
                    Cancelar Suscripción Actual
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Notificaciones */}
        <TabsContent value="notificaciones" className="space-y-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                Preferencias de Notificaciones
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Configura cómo quieres recibir alertas y recordatorios
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Notificaciones por Email */}
              <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${notificationsEnabled ? 'bg-amber-500/20' : 'bg-zinc-700'}`}>
                    {notificationsEnabled ? (
                      <Bell className="w-6 h-6 text-amber-500" />
                    ) : (
                      <BellOff className="w-6 h-6 text-zinc-500" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">Notificaciones por Email</p>
                    <p className="text-sm text-zinc-400">
                      Recibe alertas de pagos pendientes y recordatorios importantes
                    </p>
                  </div>
                </div>
                <Switch
                  checked={notificationsEnabled}
                  onCheckedChange={toggleNotifications}
                  disabled={loadingNotifications}
                  className="data-[state=checked]:bg-amber-500 scale-125"
                  data-testid="notifications-toggle"
                />
              </div>

              {/* Información adicional */}
              <div className="p-4 bg-zinc-800/30 rounded-xl border border-zinc-700">
                <h4 className="text-white font-medium mb-3">¿Qué notificaciones recibirás?</h4>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Recordatorios de pagos próximos a vencer
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Alertas de pagos vencidos
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Confirmación de pagos registrados
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Resumen semanal de cobranza
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-500" />
              Invitar Usuario
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Correo electrónico</Label>
              <Input
                type="email"
                placeholder="correo@ejemplo.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                data-testid="invite-email-input"
              />
            </div>
            
            <div>
              <Label className="text-zinc-400">Rol</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="admin" className="text-white">
                    <div className="flex items-center gap-2">
                      <Edit className="w-4 h-4 text-blue-400" />
                      Administrador - Puede crear y editar
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer" className="text-white">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-zinc-400" />
                      Visualizador - Solo puede ver
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-zinc-400">Acceso a Fraccionamiento</Label>
              <Select value={inviteFraccionamiento} onValueChange={setInviteFraccionamiento}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white" data-testid="invite-fracc-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="todos" className="text-white">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-amber-400" />
                      Todos los fraccionamientos
                    </div>
                  </SelectItem>
                  {fraccionamientos.map((fracc) => (
                    <SelectItem key={fracc.fraccionamiento_id} value={fracc.fraccionamiento_id} className="text-white">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-zinc-400" />
                        Solo: {fracc.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">
                {inviteFraccionamiento === 'todos' 
                  ? 'Podrá ver información de todos los fraccionamientos' 
                  : 'Solo podrá ver información del fraccionamiento seleccionado'}
              </p>
            </div>
            
            <div className="p-3 bg-zinc-800 rounded-lg">
              <p className="text-sm text-zinc-400">
                Se enviará un correo con un enlace de invitación que expira en 7 días.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteModal(false)}
              className="border-zinc-700 text-zinc-400 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSendInvite}
              disabled={sendingInvite || !inviteEmail}
              className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              data-testid="send-invite-btn"
            >
              {sendingInvite ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar Invitación
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
